/**
 * Screen Wake Lock while any chat is streaming. Prevents the phone from
 * blanking the display mid-stream, which on Android/iOS PWAs can suspend
 * the WebSocket and delay chunk delivery.
 *
 * Graceful no-op if the Wake Lock API is unavailable or denied (older
 * browsers, low-power mode). Reacquires after `visibilitychange` since a
 * backgrounded tab automatically releases the sentinel.
 */

import { effect } from '@preact/signals';
import { chatStreamStates } from '../../modules/chat/chat-state';

type WakeLockSentinel = { release: () => Promise<void> };
type NavigatorWithWakeLock = Navigator & {
  wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinel> };
};

let _sentinel: WakeLockSentinel | null = null;
let _acquiring = false;
let _started = false;

function anyStreaming(): boolean {
  const states = chatStreamStates.value;
  for (const id in states) {
    if (states[id].streaming) return true;
  }
  return false;
}

async function acquire(): Promise<void> {
  const nav = navigator as NavigatorWithWakeLock;
  if (!nav.wakeLock || _sentinel || _acquiring) return;
  _acquiring = true;
  try {
    const sentinel = await nav.wakeLock.request('screen');
    _sentinel = sentinel;
  } catch {
    _sentinel = null;
  } finally {
    _acquiring = false;
  }
}

async function release(): Promise<void> {
  // Null the slot synchronously so anyone racing with us (e.g. the
  // visibility handler about to call acquire()) sees "no sentinel" and can
  // request a fresh one without being blocked by the old reference.
  const sentinel = _sentinel;
  _sentinel = null;
  if (!sentinel) return;
  try {
    await sentinel.release();
  } catch {
    // ignore
  }
}

/**
 * Start watching stream state. Acquires a wake lock whenever any chat is
 * streaming and releases it when all streams idle. Idempotent.
 */
export function startWakeLockWatcher(): void {
  if (_started) return;
  _started = true;

  effect(() => {
    if (anyStreaming()) {
      acquire();
    } else {
      release();
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && anyStreaming()) {
      acquire();
    }
  });
}
